




































































import { PropType } from 'vue';
import {
  Ref,
  defineComponent,
  toRefs,
  computed,
  ref,
  watch,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api';
import {
  AdgroupFormData,
  CampaignFormData,
  InterestKeywordsInfoMap,
  InterestTargetType,
  TargetingKeywordsSearchStatus,
} from '@tt4b/ttam-enums';
import Interest from './index';
import Container from '@/components/common/behavior-container/index.vue';
import { i18n } from './../../../i18n';
import { Message } from 'byted-vui';
import { getCurrentLang } from '@tt4b/ttam-utils';
import { uniq } from 'lodash-es';
import { trackEvent } from './../../../utils/index';
import { useAdgroupAlertInfo, useStore } from '@tt4b/ttam-hooks';

function useRemoveOffline(options: {
  adgroupData: Ref<AdgroupFormData>;
  interestKeywordsInfoMap: Ref<InterestKeywordsInfoMap>;
  onFormDataChange: (data: Partial<AdgroupFormData>) => void;
}) {
  const { adgroupData, interestKeywordsInfoMap, onFormDataChange } = options;
  // 移除下线关键词逻辑
  const { tags: offlineTags } = Interest.useOfflineAlertPropsTags({
    interestKeywordsInfoMap,
    adgroupData,
  });
  const { tags: legalOfflineTags } = Interest.useLegalOfflineAlertPropsTags({
    interestKeywordsInfoMap,
    adgroupData,
  });
  const offlineAlertVisible = computed(() => offlineTags.value.length > 0);
  const offlineVisible = ref(false);
  const legalOfflineAlertVisible = computed(() => legalOfflineTags.value.length > 0);
  const legalOfflineVisible = ref(false);
  const { addAlertInfo, alertInfo, updateAlertInfo } = useAdgroupAlertInfo();
  const createElement = getCurrentInstance()?.proxy.$createElement;
  function setRemovedKeywordsAlertInfo(status: TargetingKeywordsSearchStatus) {
    if (!createElement) {
      return;
    }

    let reason, count, visibleRef;
    if (status === TargetingKeywordsSearchStatus.OFFLINE) {
      reason = i18n.t('adgroup_target_interest_auto_remove_hint_reason_drop');
      count = offlineTags.value.length;
      visibleRef = offlineVisible;
    } else if (status === TargetingKeywordsSearchStatus.LEGAL_OFFLINE) {
      reason = i18n.t('adgroup_target_interest_auto_remove_hint_reason_legal');
      count = legalOfflineTags.value.length;
      visibleRef = legalOfflineVisible;
    }

    const key = `interest-keywords-${status}`;

    const alertInfoItem = {
      key,
      message: {
        label: i18n.t('module_adgroup_behavior_interest_label'),
        // this.$t('adgroup_target_interest_auto_remove_hint')
        content: createElement('i18n', { attrs: { path: 'adgroup_target_interest_auto_remove_hint' } }, [
          createElement('span', {
            attrs: { place: 'reason' },
            domProps: { innerText: reason },
          }),
          createElement('a', {
            on: {
              click: () => {
                visibleRef.value = true;
              },
            },
            attrs: { place: 'link' },
            domProps: { innerText: i18n.t('adgroup_target_interest_auto_remove_hint_link_plural', { num: count }) },
          }),
        ]),
      },
    };

    if (alertInfo.value.find(o => o.key === key)) {
      updateAlertInfo(alertInfoItem);
      return;
    }

    addAlertInfo(alertInfoItem);
  }

  watch(
    [offlineTags, legalOfflineTags],
    () => {
      if (!offlineTags.value?.length && !legalOfflineTags.value?.length) {
        return;
      }

      const itemToDeleteIndexSet = new Set();

      adgroupData.value.interestKeywordsI18n.forEach((id, index) => {
        if (offlineTags.value?.find?.(o => Number(o.keyword_id) === Number(id))) {
          itemToDeleteIndexSet.add(index);
        } else if (legalOfflineTags.value?.find?.(o => Number(o.keyword_id) === Number(id))) {
          itemToDeleteIndexSet.add(index);
        }
      });

      onFormDataChange({
        interestKeywordsI18n: adgroupData.value.interestKeywordsI18n.filter(
          ($, index) => !itemToDeleteIndexSet.has(index),
        ),
        interestKeywordsLangI18n: adgroupData.value.interestKeywordsLangI18n.filter(
          ($, index) => !itemToDeleteIndexSet.has(index),
        ),
      });

      if (offlineTags.value.length) {
        setRemovedKeywordsAlertInfo(TargetingKeywordsSearchStatus.OFFLINE);
      }
      if (legalOfflineTags.value.length) {
        setRemovedKeywordsAlertInfo(TargetingKeywordsSearchStatus.LEGAL_OFFLINE);
      }
    },
    {
      immediate: true,
    },
  );

  return {
    offlineTags,
    legalOfflineTags,
    offlineAlertVisible,
    offlineVisible,
    legalOfflineAlertVisible,
    legalOfflineVisible,
  };
}

const name = 'ttam-interests-compose';
export default defineComponent({
  name,
  components: {
    Container,
    TtamInterestsOfflineKeywordsAlert: Interest.TtamInterestsOfflineKeywordsAlert,
    TtamInterestsLegalOfflineKeywordsAlert: Interest.TtamInterestsLegalOfflineKeywordsAlert,
    TtamInterestsLabel: Interest.TtamInterestsLabel,
    TtamInterestsTargetType: Interest.TtamInterestsTargetType,
    TtamInterestsInMarketCategory: Interest.TtamInterestsInMarketCategory,
    TtamInterestsCommonInterestsCategory: Interest.TtamInterestsCommonInterestsCategory,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const { adgroupData, campaignData } = toRefs(props);

    const adTagV2 = computed(() => adgroupData.value.adTagV2);

    const _targetType = ref(InterestTargetType.CommonInterest);
    const { contentVisible, title, desc, hasOperated, selected } = Interest.useContainerProps({
      adgroupData,
      targetType: _targetType,
    });

    const isCommonInterestTarget = computed(() => _targetType.value === InterestTargetType.CommonInterest);

    const _selected = computed({
      get: () => selected.value ?? [],
      set: v => {
        emit('common-interest-tags-change', v);
      },
    });

    const _interestKeywordsI18n = computed({
      get: () => adgroupData?.value?.interestKeywordsI18n || [],
      set: v => {
        emit('interest-keywords-i18n-change', v);
      },
    });

    const _interestKeywordsLangI18n = computed({
      get: () => adgroupData?.value?.interestKeywordsLangI18n || [],
      set: v => {
        emit('interest-keywords-lang-i18n-change', v);
      },
    });

    const _inMarketTags = computed({
      get: () => adgroupData?.value?.inMarketTags || [],
      set: v => {
        emit('in-market-tags-change', v);
      },
    });

    /** watch 兴趣定向类型  */
    watch(_targetType, newVal => {
      let count = 0;
      if (newVal === InterestTargetType.CommonInterest) {
        count = _inMarketTags?.value?.length ?? 0;
        _inMarketTags.value = [];
      } else {
        count = adTagV2.value.length + _interestKeywordsI18n.value.length;
        _selected.value = [];
        _interestKeywordsI18n.value = [];
        _interestKeywordsLangI18n.value = [];
      }
      if (count) {
        Message.success(i18n.t('module_adgroup_target_interest_remove', { num: count }) as string);
      }
    });
    /**  购买意向逻辑 */
    const { visible: targetTypeVisible } = Interest.useTargetTypePropsVisible({
      adgroupData,
      campaignData,
    });
    watch(
      targetTypeVisible,
      newVal => {
        const { InMarket, CommonInterest } = InterestTargetType;
        if (newVal) {
          _targetType.value = !adTagV2.value.length && _inMarketTags.value.length ? InMarket : CommonInterest;
        } else {
          _targetType.value = CommonInterest;
        }
      },
      {
        // 回填数据时需要初始化
        immediate: true,
      },
    );

    const { options: inMarketOptions } = Interest.useInMarketCategoryPropsOptions({
      adgroupData,
      campaignData,
    });

    const { isLoading: inMarketSearchLoading, run: inMarketSearch } =
      Interest.useInMarketInterestSearchResultDataSource({
        campaignData,
      });

    const { isLoading: inMarketLoading, run: inMarketListRun } = Interest.useInMarketInterestOptionsDataSource({
      campaignData,
      adgroupData,
    });

    const onInMarketSearch = keyword => inMarketSearch(keyword);

    /** 大众兴趣逻辑  */

    const { options: commonInterestsOptions } = Interest.useCommonInterestsCategoryPropsOptions();
    const isInterestKeywordsAvailable = Interest.useIsInterestKeywordsAvailable();
    const {
      isLoading: commonInterestsLoading,
      run: commonInterestsListRun,
      data: interestOptions,
    } = Interest.useCommonInterestsInterestOptionsDataSource({
      campaignData,
      adgroupData,
    });

    const { run: commonInterestsKeywordsInfoRun } = Interest.useCommonInterestsInterestKeywordsInfoDataSource({
      campaignData,
      adgroupData,
    });

    const { state } = useStore();

    const interestKeywordsInfoMap = computed(() => state.targetInfo.value?.interestKeywordsInfoMap ?? {});
    const {
      run: commonInterestsSearch,
      isLoading: commonInterestsSearchLoading,
      data: keywordSearchResult,
      keyword: searchKeyword,
    } = Interest.useCommonInterestsInterestSearchResultDataSource({
      campaignData,
      adgroupData,
    });

    const { data: recommendKeywords } = Interest.useCommonInterestsRecommendKeywordsDataSource({
      adgroupData,
      campaignData,
    });

    const onCommonInterestsSearch = keyword => commonInterestsSearch(keyword);
    const { mergeInterestCategoryChildren } = Interest.useInterestCategoryChildrenMerger({
      interestOptions: commonInterestsOptions,
    });
    const { filterMethod } = Interest.useCommonInterestsCategoryPropsFilterMethod({
      interestOptions: commonInterestsOptions,
    });
    const onCommonInterestsCategoryChange = selection => {
      if (isInterestKeywordsAvailable.value) {
        let categoryIds: number[] = [];
        const keywordIds: number[] = [];
        const keywordLangs: string[] = [];

        selection.forEach(item => {
          if (typeof item === 'string') {
            // is interest keyword(id type is `${id}-keyword`)
            const id = Number(item.split('-')[0]);
            keywordIds.push(id);
            keywordLangs.push(interestKeywordsInfoMap.value[id].lang || getCurrentLang());
          } else {
            // is interest category(id type is number)
            categoryIds.push(item);
          }
        });

        if (!interestOptions.value?.length) {
          categoryIds = uniq(categoryIds);
        } else {
          categoryIds = mergeInterestCategoryChildren(categoryIds);
        }

        _selected.value = categoryIds;
        _interestKeywordsI18n.value = keywordIds;
        _interestKeywordsLangI18n.value = keywordLangs;
      } else {
        _selected.value = selection;
      }
    };

    const onCommonInterestNodeHover = val => {
      trackEvent('create_campaign_targeting_interest_searchresult_impression', {
        keyword: searchKeyword.value,
        number: keywordSearchResult.value.length,
        adgroup_id: adgroupData.value?.adId || '',
      });
    };

    const onCommonInterestCloseOne = () => {
      trackEvent('create_campaign_targeting_interest_searchresult_cross_click', {
        number: keywordSearchResult.value.length,
        adgroup_id: adgroupData.value?.adId || '',
      });
    };

    const onKeywordsChange = val => {
      _interestKeywordsI18n.value = val;
    };

    const onKeywordsLangChange = val => {
      _interestKeywordsLangI18n.value = val;
    };

    onMounted(() => {
      commonInterestsListRun();
      commonInterestsKeywordsInfoRun();
      if (targetTypeVisible.value) {
        inMarketListRun();
      }
    });
    watch(targetTypeVisible, val => {
      if (val) {
        inMarketListRun();
      }
    });

    const onRemoveAll = () => {
      onCommonInterestsCategoryChange([]);
      _inMarketTags.value = [];
    };

    const {
      offlineTags,
      legalOfflineTags,
      offlineAlertVisible,
      offlineVisible,
      legalOfflineAlertVisible,
      legalOfflineVisible,
    } = useRemoveOffline({
      adgroupData,
      interestKeywordsInfoMap,
      onFormDataChange: data => {
        _interestKeywordsI18n.value = data.interestKeywordsI18n;
        _interestKeywordsLangI18n.value = data.interestKeywordsLangI18n;
      },
    });

    return {
      name,
      _targetType,
      _inMarketTags,
      _selected,
      contentVisible,
      title,
      desc,
      hasOperated,
      isCommonInterestTarget,
      targetTypeVisible,
      onInMarketSearch,
      inMarketOptions,
      inMarketSearchLoading,
      inMarketLoading,
      onRemoveAll,

      commonInterestsOptions,
      commonInterestsLoading,
      onCommonInterestsSearch,
      commonInterestsSearchLoading,
      onCommonInterestsCategoryChange,
      filterMethod,
      recommendKeywords,
      onKeywordsChange,
      onKeywordsLangChange,

      onCommonInterestNodeHover,
      onCommonInterestCloseOne,

      offlineTags,
      legalOfflineTags,
      offlineAlertVisible,
      offlineVisible,
      legalOfflineAlertVisible,
      legalOfflineVisible,
    };
  },
});
